// src/context/CheckoutContext.js

import React, { createContext, useState, useContext } from "react";

const CheckoutContext = createContext();

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
};

export const CheckoutProvider = ({ children }) => {
  // Shipping Address State
  const [shippingAddress, setShippingAddress] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "Punjab",
    pincode: "",
    addressType: "Home",
  });

  // Billing Address State
  const [billingAddress, setBillingAddress] = useState({
    sameAsShipping: true,
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "Punjab",
    pincode: "",
  });

  // Order State
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending"); // pending, success, failed
  const [orderId, setOrderId] = useState(null);

  // Update shipping address
  const updateShippingAddress = (field, value) => {
    setShippingAddress((prev) => ({ ...prev, [field]: value }));
  };

  // Update billing address
  const updateBillingAddress = (field, value) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  // Toggle same as shipping
  const toggleSameAsShipping = () => {
    setBillingAddress((prev) => ({
      ...prev,
      sameAsShipping: !prev.sameAsShipping,
    }));
  };

  // Validate shipping address
  const validateShippingAddress = () => {
    const { fullName, email, phone, address, city, pincode } = shippingAddress;

    if (!fullName.trim())
      return { valid: false, message: "Full name is required" };
    if (!email.trim() || !email.includes("@"))
      return { valid: false, message: "Valid email is required" };
    if (!phone.trim() || phone.length < 10)
      return { valid: false, message: "Valid phone number is required" };
    if (!address.trim())
      return { valid: false, message: "Address is required" };
    if (!city.trim()) return { valid: false, message: "City is required" };
    if (!pincode.trim() || pincode.length !== 5)
      return { valid: false, message: "Valid 5-digit pincode is required" };

    return { valid: true };
  };

  // Create order
  const createOrder = (cartData) => {
    const newOrderId = "ORD" + Date.now();
    const order = {
      orderId: newOrderId,
      date: new Date().toISOString(),
      shippingAddress,
      billingAddress: billingAddress.sameAsShipping
        ? shippingAddress
        : billingAddress,
      items: cartData.items,
      subtotal: cartData.subtotal,
      discount: cartData.discount,
      shipping: cartData.shipping,
      tax: cartData.tax,
      total: cartData.total,
      status: "pending",
    };

    setOrderDetails(order);
    setOrderId(newOrderId);
    return order;
  };

  // Update payment status
  const updatePaymentStatus = (status) => {
    setPaymentStatus(status);
    if (orderDetails) {
      setOrderDetails((prev) => ({ ...prev, paymentStatus: status }));
    }
  };

  // Reset checkout
  const resetCheckout = () => {
    setShippingAddress({
      fullName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "Punjab",
      pincode: "",
      addressType: "Home",
    });
    setBillingAddress({
      sameAsShipping: true,
      fullName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "Punjab",
      pincode: "",
    });
    setOrderDetails(null);
    setPaymentStatus("pending");
    setOrderId(null);
  };

  const value = {
    shippingAddress,
    billingAddress,
    orderDetails,
    paymentStatus,
    orderId,
    updateShippingAddress,
    updateBillingAddress,
    toggleSameAsShipping,
    validateShippingAddress,
    createOrder,
    updatePaymentStatus,
    resetCheckout,
  };

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
